/**
 * DocHistory — Version history table for a document.
 *
 * Shows all published versions with dates, authors, and status.
 * Clickable rows link to the specific historical version view.
 *
 * Route: /docs/:slug/history
 * API: GET /@docs/versions?slug=...
 *
 * Ref: CR-2026-025
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Label,
  Loader,
  Icon,
  Breadcrumb,
  Message,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

export default function DocHistory() {
  const params = useParams();
  const slug = params.slug || params[0] || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    fetchApi(`${getApiPath()}/@docs/versions?slug=${encodeURIComponent(slug)}`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Loader active inline="centered" content="Loading version history..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const doc = data?.document;
  const versions = data?.versions || [];
  const activeDraft = data?.active_draft;

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '900px' }}>
      <Helmet title={`Version History — ${doc?.title || slug}`} />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/docs">Knowledge Base</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to={`/docs/${slug}`}>
          {doc?.title || slug}
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Version History</Breadcrumb.Section>
      </Breadcrumb>

      <Header as="h1">
        <Icon name="history" />
        <Header.Content>
          Version History
          <Header.Subheader>{doc?.title}</Header.Subheader>
        </Header.Content>
      </Header>

      {/* Active draft banner */}
      {activeDraft && (
        <Message color="blue" icon>
          <Icon name="edit" />
          <Message.Content>
            <Message.Header>Draft in Progress</Message.Header>
            Version {activeDraft.version} is being drafted
            (status: {activeDraft.status}).
            {' '}
            <Link to={`/docs/${slug}/edit`}>Open editor</Link>
          </Message.Content>
        </Message>
      )}

      {versions.length === 0 ? (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="history" />
            No version history yet
          </Header>
          <p>
            Version history will appear here after the first draft is approved.
          </p>
        </Segment>
      ) : (
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Version</Table.HeaderCell>
              <Table.HeaderCell>Published</Table.HeaderCell>
              <Table.HeaderCell>Author</Table.HeaderCell>
              <Table.HeaderCell>Approved By</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {versions.map((v) => (
              <Table.Row key={v.id}>
                <Table.Cell>
                  <Link to={`/docs/${slug}/version?v=${encodeURIComponent(v.version)}`}>
                    <strong>{v.version}</strong>
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  {v.published_at
                    ? new Date(v.published_at).toLocaleDateString()
                    : '—'}
                </Table.Cell>
                <Table.Cell>{v.author || '—'}</Table.Cell>
                <Table.Cell>{v.published_by || '—'}</Table.Cell>
                <Table.Cell>
                  {v.status === 'effective' ? (
                    <Label color="green" size="small">
                      <Icon name="circle" /> Effective
                    </Label>
                  ) : (
                    <Label basic size="small">
                      Superseded
                      {v.superseded_by && ` → v${v.superseded_by}`}
                    </Label>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Segment secondary size="small" style={{ marginTop: '2em' }}>
        <Icon name="info circle" />
        This document is controlled per SOP-DC-001.
        Verify currency against the Master Document Register.
      </Segment>
    </Container>
  );
}
